package com.eladfinish.bible.contest.utils

/**
 * Unescapes Java-style escaped characters in a string.
 *
 * Supports:
 *  - Standard escapes: \n, \t, \r, \b, \f, \", \', \\
 *  - Unicode escapes: \uXXXX
 *  - Octal escapes: \0 to \377
 *
 * If the input is null, returns null.
 */
fun unescapeJava(input: String?): String? {
    if (input == null) return null

    val out = StringBuilder(input.length)
    var i = 0

    while (i < input.length) {
        val ch = input[i]

        if (ch != '\\') {
            out.append(ch)
            i++
            continue
        }

        // Backslash found
        if (i + 1 >= input.length) {
            out.append('\\')
            break
        }

        when (val next = input[i + 1]) {
            '\\' -> {
                out.append('\\')
                i += 2
            }

            '\'' -> {
                out.append('\'')
                i += 2
            }

            '"' -> {
                out.append('"')
                i += 2
            }

            'n' -> {
                out.append('\n')
                i += 2
            }

            'r' -> {
                out.append('\r')
                i += 2
            }

            't' -> {
                out.append('\t')
                i += 2
            }

            'b' -> {
                out.append('\b')
                i += 2
            }

            'f' -> {
                out.append('\u000C') // form feed
                i += 2
            }

            'u' -> {
                // Unicode escape: \uXXXX
                if (i + 5 >= input.length) {
                    // Invalid unicode escape, keep as-is
                    out.append("\\u")
                    i += 2
                } else {
                    val hex = input.substring(i + 2, i + 6)
                    try {
                        val code = hex.toInt(16)
                        out.append(code.toChar())
                        i += 6
                    } catch (_: NumberFormatException) {
                        // Invalid hex, keep literally
                        out.append("\\u").append(hex)
                        i += 6
                    }
                }
            }

            in '0'..'7' -> {
                // Octal escape: \0 to \377
                var j = i + 1
                var octal = 0
                var count = 0

                while (j < input.length && count < 3 && input[j] in '0'..'7') {
                    octal = octal * 8 + (input[j] - '0')
                    j++
                    count++
                }

                out.append(octal.toChar())
                i = j
            }

            else -> {
                // Unknown escape, keep character as-is
                out.append(next)
                i += 2
            }
        }
    }

    return out.toString()
}
